import React from "react";
import { createPortal } from "react-dom";
import { X, Building2, FileText, Calendar, DollarSign, MapPin, User, Clock, CheckCircle, AlertCircle, Info, HelpCircle } from "lucide-react";
import { Button } from "@/Components/ui/button";
import { Badge } from "@/Components/ui/badge";
import { format, parseISO, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";

const PartnershipDetailsModal = ({ partnership, onClose }) => {
  useBodyScrollLock();
  if (!partnership) return null;

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Não informado';
    try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
      return isValid(date) ? format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR }) : 'Data inválida';
    } catch {
      return 'Data inválida';
    }
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
      return isValid(date) ? format(date, 'dd/MM/yyyy', { locale: ptBR }) : 'N/A';
    } catch {
      return 'N/A';
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'no_prazo':
        return {
          label: 'No Prazo',
          color: 'bg-blue-100 text-blue-800 border-blue-300',
          icon: Clock,
          description: 'Parceria dentro do prazo estabelecido'
        };
      case 'quitado':
        return {
          label: 'Quitado',
          color: 'bg-green-100 text-green-800 border-green-300',
          icon: CheckCircle,
          description: 'Prestação de contas aprovada e quitada'
        };
      case 'em_debito':
        return {
          label: 'Em Débito',
          color: 'bg-red-100 text-red-800 border-red-300',
          icon: AlertCircle,
          description: 'Parceria com pendências ou em débito'
        };
      case 'verificar':
        return {
          label: 'Verificar',
          color: 'bg-amber-100 text-amber-800 border-amber-300',
          icon: Info,
          description: 'Aguardando verificação ou análise'
        };
      default:
        return {
          label: 'Indefinido',
          color: 'bg-gray-100 text-gray-800 border-gray-300',
          icon: Info,
          description: 'Status não definido'
        };
    }
  };

  const getTipoParceiraColor = (tipo) => {
    switch (tipo) {
      case 'Termo de Colaboração':
      case 'Termo de Colaboração - Emenda Impositiva':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'Termo de Fomento':
      case 'Termo de Fomento - Emenda Impositiva':
        return 'bg-indigo-100 text-indigo-800 border-indigo-300';
      case 'Acordo de Cooperação':
        return 'bg-teal-100 text-teal-800 border-teal-300';
      case 'Subvenção Social':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const statusConfig = getStatusConfig(partnership.status || partnership.status_automatico);
  const StatusIcon = statusConfig.icon;

  // Portal: escapa do fluxo normal da página, evitando que containers com
  // margin/spacing (ex.: space-y-*) desloquem o overlay fixed.
  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        >
          {/* Header com gradiente */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Detalhes da Parceria</h2>
                <p className="text-blue-100 text-sm">Informações completas</p>
              </div>
            </div>
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 rounded-full h-8 w-8 p-0"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Conteúdo com scroll */}
          <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
            {/* OSC e Status */}
            <div className="mb-6 p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg border border-slate-200">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    {partnership.osc || 'Não informado'}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <FileText className="w-4 h-4" />
                    <span className="font-mono">{partnership.cnpj || 'CNPJ não informado'}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Badge className={`${statusConfig.color} border-2 px-3 py-1 text-sm font-semibold flex items-center gap-2`}>
                    <StatusIcon className="w-4 h-4" />
                    {statusConfig.label}
                  </Badge>
                  {partnership.tipo_parceria && (
                    <Badge className={`${getTipoParceiraColor(partnership.tipo_parceria)} border-2 px-3 py-1 text-xs`}>
                      {partnership.tipo_parceria}
                    </Badge>
                  )}
                </div>
              </div>
              <p className="text-sm text-slate-600 italic flex items-center gap-2">
                <Info className="w-4 h-4" />
                {statusConfig.description}
              </p>
            </div>

            {/* Grid de informações */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Informações Básicas */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-slate-900 flex items-center gap-2 pb-2 border-b-2 border-blue-200">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Informações Básicas
                </h4>
                
                <InfoItem
                  label="Processo/Ano"
                  value={partnership.processo_ano || 'Não informado'}
                  icon={FileText}
                />
                
                <InfoItem
                  label="Ano"
                  value={partnership.ano || 'Não informado'}
                  icon={Calendar}
                />
                
                <InfoItem
                  label="Origem"
                  value={partnership.origem || 'Não informado'}
                  icon={MapPin}
                />
              </div>

              {/* Valores e Datas */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-slate-900 flex items-center gap-2 pb-2 border-b-2 border-green-200">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  Valores e Prazos
                </h4>
                
                <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
                  <div className="text-sm text-green-700 font-medium mb-1">Valor Total</div>
                  <div className="text-2xl font-bold text-green-800">
                    {formatCurrency(partnership.valor)}
                  </div>
                </div>
                
                <InfoItem
                  label="Data de Início"
                  value={formatDate(partnership.data_inicio)}
                  icon={Calendar}
                  highlight="blue"
                />
                
                <InfoItem
                  label="Data de Fim"
                  value={formatDate(partnership.data_fim)}
                  icon={Calendar}
                  highlight="red"
                />
                
                {/* Data Final Calculada ou "Não Calculado" com tooltip */}
                <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-700">
                      Data Final Calculada (Prazo: {partnership.prazos || 'N/A'})
                    </span>
                  </div>
                  {partnership.data_final_calculada ? (
                    <div className="text-lg font-bold text-amber-800">
                      {formatDate(partnership.data_final_calculada)}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-amber-800">Não Calculado</span>
                      <div className="group relative">
                        <HelpCircle className="w-4 h-4 text-amber-600 cursor-help" />
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 p-3 bg-slate-800 text-white text-xs rounded-lg shadow-lg z-10">
                          <div className="font-semibold mb-1">Por que "Não Calculado"?</div>
                          <p>Não foi informada Data Inicial e/ou Data Final para o cálculo do prazo.</p>
                          <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800"></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Informações Adicionais */}
            <div className="space-y-4 mb-6">
              <h4 className="text-lg font-semibold text-slate-900 flex items-center gap-2 pb-2 border-b-2 border-purple-200">
                <User className="w-5 h-5 text-purple-600" />
                Informações Complementares
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoItem
                  label="Local do Arquivamento"
                  value={partnership.local_f || 'Não informado'}
                  icon={MapPin}
                />
                
                <InfoItem
                  label="Responsável"
                  value={partnership.responsavel || 'Não informado'}
                  icon={User}
                />
              </div>
            </div>

            {/* Análises e Observações */}
            {partnership.analises_observacoes && (
              <div className="space-y-3">
                <h4 className="text-lg font-semibold text-slate-900 flex items-center gap-2 pb-2 border-b-2 border-amber-200">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  Análises e Observações
                </h4>
                <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                    {partnership.analises_observacoes}
                  </p>
                </div>
              </div>
            )}

            {/* Metadados */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-slate-500">
                <div>
                  <div className="font-medium text-slate-600 mb-1">Criado em</div>
                  <div>{formatDateShort(partnership.createdAt)}</div>
                </div>
                <div>
                  <div className="font-medium text-slate-600 mb-1">Atualizado em</div>
                  <div>{formatDateShort(partnership.updatedAt)}</div>
                </div>
                <div>
                  <div className="font-medium text-slate-600 mb-1">Status da Parceria</div>
                  <div className="capitalize">{getStatusConfig(partnership.status || partnership.status_automatico).label}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end">
            <Button
              onClick={onClose}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6"
            >
              Fechar
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

// Componente auxiliar para itens de informação
const InfoItem = ({ label, value, icon: Icon, multiline = false, highlight = null }) => {
  const highlightColors = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    red: 'bg-red-50 border-red-200',
    amber: 'bg-amber-50 border-amber-200'
  };

  const bgClass = highlight ? highlightColors[highlight] : 'bg-slate-50 border-slate-200';

  return (
    <div className={`p-3 rounded-lg border ${bgClass} transition-all duration-200 hover:shadow-sm`}>
      <div className="flex items-start gap-2">
        {Icon && <Icon className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-slate-600 mb-1">{label}</div>
          <div className={`text-sm text-slate-900 ${multiline ? 'whitespace-pre-wrap' : 'truncate'}`}>
            {value}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnershipDetailsModal;
